/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { Pencil, Play, RefreshCw, Save } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '../../api';
import type {
  IntegrationEnvironment,
  IntegrationProvider,
  ProposeProviderSettingBody,
  ProviderDefinition,
  ProviderFieldDefinition,
  ProviderSettingDetail,
  ProviderSettingVersion,
  SponsoringRegistrar,
  SponsoringRegistrarBody,
} from '../../api/areas/integrations';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { useRetained, useWrite } from '../../components/Write';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ASK_AFTER_FAILURES, AskWhenField, askWhenText } from './captchaFields';
import { PendingChangesPanel, PROVIDER_CHANGE_KINDS } from './changes';
import {
  AppliedByList,
  ChangeDialog,
  ConfirmCheck,
  ConnectionLink,
  DeploymentNote,
  emptySecret,
  EnvironmentBanner,
  IntegrationError,
  NumberField,
  OnOffBadge,
  Panel,
  PresetFill,
  ReasonField,
  secretDraftValid,
  SecretField,
  secretInput,
  SecretStatusLine,
  ServiceName,
  StaffName,
  StatusBadge,
  SwitchRow,
  tx,
  useIntegrationContext,
  When,
  WithIntegrationContext,
  WriteOutcome,
  type SecretDraft,
} from './shared';
import { ProviderTestDialog, TestList } from './tests';
import { VnPlacesPanel } from './VnPlacesPanel';
import { WhoisCertificatePanel } from './WhoisCertificatePanel';

const TAB_OF: Record<string, IntegrationProvider> = {
  registrar: 'VNNIC_REGISTRAR_API',
  ekyc: 'VNNIC_EKYC',
  hstm: 'VNNIC_HSTM',
  gtld: 'VNNIC_GTLD_REPORTING',
  whois: 'VNNIC_WHOIS',
};
const TAB_KEYS = Object.keys(TAB_OF);

/** `/settings/vnnic` — VNNIC Registrar API, eKYC, dossier (HSTM), gTLD reporting and the WHOIS
 * service: this server's only configuration of each (ADR-018). */
export function VnnicPage() {
  const { t } = useTranslation('integrations');
  useDocumentTitle(t('vnnic.title'));
  return (
    <>
      <PageHeader title={t('vnnic.title')} subtitle={t('vnnic.subtitle')} />
      <WithIntegrationContext>
        <VnnicView />
      </WithIntegrationContext>
    </>
  );
}

function VnnicView() {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const [params, setParams] = useSearchParams();
  const [tick, setTick] = useState(0);
  const catalog = useApi((signal) => api.getProviderCatalog({ signal }), []);
  const tab = TAB_KEYS.includes(params.get('tab') ?? '') ? (params.get('tab') as string) : 'registrar';
  const env = ctx.integration_environment;
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };
  const reload = () => setTick((n) => n + 1);
  return (
    <div className="space-y-6">
      <DeploymentNote providers />
      <PendingChangesPanel kinds={PROVIDER_CHANGE_KINDS} refreshKey={tick} onChanged={reload} />
      {catalog.status === 'error' ? (
        <DataError error={catalog.error} onRetry={catalog.reload} title={t('vnnic.catalogFailed')} />
      ) : catalog.status === 'loading' ? (
        <Loading rows={3} />
      ) : (
        <Tabs value={tab} onValueChange={(v) => setParam('tab', v)}>
          <div>
            <TabsList className="grid h-auto w-full grid-cols-2 sm:flex sm:w-fit">
              {TAB_KEYS.map((k) => (
                <TabsTrigger key={k} value={k} className="px-3">
                  {t(`provider.tab.${TAB_OF[k] as IntegrationProvider}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {TAB_KEYS.map((k) => {
            const provider = TAB_OF[k] as IntegrationProvider;
            const def = catalog.data.providers.find((p) => p.provider === provider);
            return (
              <TabsContent key={k} value={k} className="mt-4 min-w-0">
                {def ? <ProviderPanel def={def} env={env} tick={tick} onChanged={reload} /> : <EmptyState title={t('vnnic.unknownProvider')} />}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </div>
  );
}

function unitLabel(unit: ProviderFieldDefinition['unit']): string {
  return unit ? tx(`provider.unit.${unit}`, unit) : '';
}

function optionValue(def: ProviderFieldDefinition, value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') return <span className="text-muted-foreground">{tx('provider.default', 'default')}</span>;
  if (def.key === ASK_AFTER_FAILURES) return askWhenText((k, o) => tx(k, k, o), Number(value));
  if (def.type === 'integer') return `${formatCount(Number(value))}${def.unit ? ` ${unitLabel(def.unit)}` : ''}`;
  return <code className="break-all text-xs">{String(value)}</code>;
}

export function ProviderPanel({ def, env, tick, onChanged }: { def: ProviderDefinition; env: IntegrationEnvironment; tick: number; onChanged: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const [dialog, setDialog] = useState<'edit' | 'test' | null>(null);
  const [localTick, setLocalTick] = useState(0);
  const result = useApi((signal) => api.getProviderSetting(def.provider, { signal }), [def.provider, tick, localTick]);
  const data = useRetained(result.data);
  const detail = data && data.provider === def.provider ? data : undefined;
  const refresh = () => {
    setLocalTick((n) => n + 1);
    onChanged();
  };
  const preset = def.presets.find((p) => p.environment === env);
  const active = detail?.active ?? null;
  const pendingOpen = !!detail?.pending;
  const onEnvVars = !!detail && (!detail.configured_in_database || !active || ctx.settings_source === 'env');
  const someServicesOnEnv = !!detail?.applied_by.some((a) => a.source === 'ENVIRONMENT');
  const restartPending = !!detail?.applied_by.some((a) => a.last_error?.code === 'RESTART_REQUIRED');
  const matchesPreset = !!preset && active?.base_url === preset.base_url;
  const optionFields = def.fields.filter((f) => f.key !== 'base_url');

  if (result.status === 'error' && !detail) return <DataError error={result.error} onRetry={result.reload} title={t('vnnic.loadFailed')} />;
  if (!detail) return <Loading rows={4} />;

  return (
    <div className="min-w-0 space-y-6">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{t(`provider.name.${def.provider}`)}</h2>
        <p className="text-sm text-muted-foreground">{t(`provider.description.${def.provider}`)}</p>
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {t('vnnic.usedBy')}
          {def.used_by.map((s, i) => (
            <span key={s}>
              <ServiceName service={s} />
              {i < def.used_by.length - 1 ? ',' : null}
            </span>
          ))}
        </p>
      </div>
      <EnvironmentBanner env={env} />
      {ctx.settings_source === 'env' ? null : onEnvVars ? (
        <Callout tone="warn" title={t('vnnic.fromEnvTitle')}>
          {t('vnnic.fromEnv', { env: t(`env.inline.${env}`) })}
        </Callout>
      ) : someServicesOnEnv ? (
        <Callout tone="info">{t('vnnic.someOnEnv', { seconds: ctx.refresh_seconds })}</Callout>
      ) : null}
      {restartPending ? (
        <Callout tone="warn" title={t('vnnic.restartTitle')}>
          {t('vnnic.restart')}
        </Callout>
      ) : null}

      <Panel
        title={t('vnnic.settingsTitle')}
        description={
          def.apply_modes.enabled === 'LIVE' && def.apply_modes.settings === 'LIVE' ? (
            t('applyMode.allLive', { seconds: ctx.refresh_seconds })
          ) : (
            <span className="grid gap-0.5">
              <span className={def.apply_modes.enabled === 'RESTART' ? 'font-medium text-warning' : undefined}>{t(`applyMode.enabled.${def.apply_modes.enabled}`, { seconds: ctx.refresh_seconds })}</span>
              <span>{t(`applyMode.settings.${def.apply_modes.settings}`, { seconds: ctx.refresh_seconds })}</span>
            </span>
          )
        }
        actions={
          <>
            <Button size="sm" variant="outline" onClick={() => setDialog('test')} disabled={!active?.base_url && !detail.pending?.base_url}>
              <Play /> {t('vnnic.test')}
            </Button>
            <Button size="sm" onClick={() => setDialog('edit')} disabled={pendingOpen} title={pendingOpen ? t('envTab.blocked') : undefined}>
              <Pencil /> {detail.configured_in_database ? t('vnnic.edit') : t('vnnic.configure')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {pendingOpen ? <p className="text-xs text-muted-foreground">{t('envTab.blocked')}</p> : null}
          {preset ? (
            <p className="text-sm">
              {t('vnnic.preset', { env: t(`env.inline.${env}`) })} <code className="break-all rounded bg-muted px-1 text-xs">{preset.base_url}</code>
              {preset.note ? <span className="block text-xs text-muted-foreground">{t(`warnings.${preset.note}`)}</span> : null}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('vnnic.noPreset', { env: t(`env.inline.${env}`) })}</p>
          )}
          {active ? (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <OnOffBadge on={active.enabled} />
                <span className="text-muted-foreground">{t('envTab.versionInForce', { version: active.version })}</span>
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <StaffName staff={active.activated_by} /> · <When at={active.activated_at} />
                </span>
              </div>
              <KeyValue
                columns={2}
                rows={[
                  [
                    t('provider.field.base_url'),
                    <span key="u" className="grid gap-0.5">
                      <code className="break-all text-sm">{active.base_url ?? '—'}</code>
                      {preset ? (
                        <span className={matchesPreset ? 'text-xs text-success' : 'text-xs text-warning'}>{matchesPreset ? t('vnnic.matchesPreset') : t('vnnic.differsFromPreset')}</span>
                      ) : null}
                    </span>,
                  ],
                  ...optionFields.map((f) => [tx(`provider.field.${f.key}`, f.key), optionValue(f, active.options[f.key])] as const),
                ]}
              />
              <div className="grid items-start gap-3 sm:grid-cols-2">
                {def.secrets.map((s) => (
                  <SecretStatusLine key={s.key} label={tx(`provider.secret.${s.key}`, s.key)} status={active.secrets[s.key] ?? { configured: false, source: null, reference: null, stored_secret_id: null, changed_at: null, changed_by: null, destroyed: false }} />
                ))}
              </div>
              <p className="text-sm">
                <span className="text-muted-foreground">{t('versions.reason')}:</span> {active.reason}
              </p>
            </>
          ) : (
            <EmptyState title={t('vnnic.notConfigured', { env: t(`env.inline.${env}`) })}>{t('vnnic.notConfiguredHint')}</EmptyState>
          )}
        </div>
      </Panel>

      <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
        <Panel title={t('vnnic.applied')} description={t('vnnic.appliedHint')}>
          <AppliedByList items={detail.applied_by} activeVersion={active?.version ?? null} />
        </Panel>
        <Panel title={t('envTab.tests')} description={def.login_test ? t('vnnic.testsHintLogin') : t('vnnic.testsHint')}>
          <TestList tests={detail.recent_tests} />
        </Panel>
      </div>

      {def.provider === 'VNNIC_REGISTRAR_API' ? <VnPlacesPanel /> : null}
      {def.provider === 'VNNIC_WHOIS' ? <WhoisCertificatePanel env={env} detail={detail} onDone={refresh} /> : null}

      <Panel title={t('envTab.history')} flush>
        <ProviderHistory provider={def.provider} tick={tick + localTick} def={def} />
      </Panel>

      {def.has_sponsoring_registrars ? <SponsoringRegistrars tick={tick + localTick} onChanged={refresh} /> : null}

      <ChangeDialog open={dialog === 'edit'} onOpenChange={(o) => setDialog(o ? 'edit' : null)} title={t('providerDialog.title', { name: t(`provider.name.${def.provider}`) })} description={t('providerDialog.description')} wide>
        <ProviderForm def={def} env={env} detail={detail} onClose={() => setDialog(null)} onDone={refresh} />
      </ChangeDialog>
      <ProviderTestDialog open={dialog === 'test'} onOpenChange={(o) => setDialog(o ? 'test' : null)} provider={def.provider} loginTest={def.login_test} env={env} detail={detail} onTested={refresh} />
    </div>
  );
}

function ProviderHistory({ provider, tick, def }: { provider: IntegrationProvider; tick: number; def: ProviderDefinition }) {
  const { t } = useTranslation('integrations');
  const [before, setBefore] = useState<number[]>([]);
  const cursor = before[before.length - 1];
  const result = useApi((signal) => api.listProviderSettingVersions(provider, { before_version: cursor, limit: 10 }, { signal }), [provider, cursor, tick]);
  const data = useRetained(result.data);
  const columns: Column<ProviderSettingVersion>[] = [
    { key: 'v', header: t('versions.version'), cell: (v) => v.version },
    {
      key: 's',
      header: t('versions.status'),
      cell: (v) => (
        <div className="grid gap-1">
          <StatusBadge group="versionStatus" value={v.status} />
          {v.approval ? <StatusBadge group="approvalStatus" value={v.approval.status} /> : null}
        </div>
      ),
    },
    { key: 'e', header: t('versions.enabled'), cell: (v) => <OnOffBadge on={v.enabled} /> },
    { key: 'u', header: t('provider.field.base_url'), cell: (v) => <code className="break-all text-xs">{v.base_url ?? '—'}</code> },
    {
      key: 'sec',
      header: t('vnnic.secrets'),
      cell: (v) => (
        <ul className="grid gap-0.5 text-xs">
          {def.secrets.map((s) => {
            const st = v.secrets[s.key];
            return (
              <li key={s.key}>
                {tx(`provider.secret.${s.key}`, s.key)}:{' '}
                <span className="text-muted-foreground">{!st?.configured ? t('secret.notConfigured') : st.destroyed ? t('secret.destroyed') : st.source ? t(`secret.source.${st.source}`) : ''}</span>
              </li>
            );
          })}
        </ul>
      ),
    },
    { key: 'r', header: t('versions.reason'), cell: (v) => v.reason, className: 'min-w-40' },
    {
      key: 'by',
      header: t('versions.createdBy'),
      cell: (v) => (
        <div className="grid gap-0.5 text-xs">
          <StaffName staff={v.created_by} />
          <When at={v.created_at} />
        </div>
      ),
    },
  ];
  if (result.status === 'error') return <DataError error={result.error} onRetry={result.reload} />;
  if (!data) return <Loading rows={2} />;
  return (
    <>
      <DataTable columns={columns} rows={data.items} rowKey={(v) => v.id} empty={<p className="text-sm text-muted-foreground">{t('versions.noneProvider')}</p>} />
      {data.next_before_version !== null || before.length ? (
        <div className="flex justify-end gap-2 border-t px-4 py-3">
          <Button size="sm" variant="outline" disabled={!before.length} onClick={() => setBefore((b) => b.slice(0, -1))}>
            {t('common.newer')}
          </Button>
          <Button size="sm" variant="outline" disabled={data.next_before_version === null} onClick={() => data.next_before_version !== null && setBefore((b) => [...b, data.next_before_version as number])}>
            {t('common.older')}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function ProviderForm({ def, env, detail, onClose, onDone }: { def: ProviderDefinition; env: IntegrationEnvironment; detail: ProviderSettingDetail; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const base = detail.active;
  const preset = def.presets.find((p) => p.environment === env);
  const optionFields = def.fields.filter((f) => f.key !== 'base_url');
  const [enabled, setEnabled] = useState(base?.enabled ?? true);
  const [baseUrl, setBaseUrl] = useState(base?.base_url ?? preset?.base_url ?? '');
  const [options, setOptions] = useState<Record<string, string | number | null>>(() =>
    Object.fromEntries(optionFields.map((f) => [f.key, base?.options[f.key] ?? preset?.options[f.key] ?? f.default])),
  );
  const [secrets, setSecrets] = useState<Record<string, SecretDraft>>(() =>
    Object.fromEntries(def.secrets.map((s) => [s.key, emptySecret(!!base?.secrets[s.key]?.configured && !base.secrets[s.key]?.destroyed)])),
  );
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: ProposeProviderSettingBody, opts) => api.proposeProviderSetting(def.provider, body, opts));
  const production = env === 'PRODUCTION';
  const httpInProduction = production && baseUrl.trim().toLowerCase().startsWith('http://');
  const hasValue = (key: string) => !!base?.secrets[key]?.configured && !base.secrets[key]?.destroyed;
  // Nothing stored and nothing typed: KEEP (= still not set), accepted while the service is off
  // or the secret is optional.
  const leftEmpty = (key: string, required: boolean) => {
    const d = secrets[key];
    return !hasValue(key) && (!enabled || !required) && (!d || (d.mode === 'VALUE' && !d.value));
  };
  const secretsValid = def.secrets.every((s) => {
    const d = secrets[s.key];
    return leftEmpty(s.key, s.required_when_enabled) || (!!d && secretDraftValid(d));
  });
  const secretTouched = def.secrets.some((s) => secrets[s.key]?.mode !== 'KEEP' && !leftEmpty(s.key, s.required_when_enabled));
  const requiredOk = !enabled || (baseUrl.trim() && optionFields.every((f) => !f.required_when_enabled || f.default !== null || (options[f.key] !== null && options[f.key] !== '')));
  const valid = requiredOk && !httpInProduction && secretsValid && reason.trim() && confirmed;
  const advanced = optionFields.filter((f) => f.advanced);
  const basic = optionFields.filter((f) => !f.advanced);

  const submit = async () => {
    const res = await write.run({
      expected_generation: detail.generation,
      base_version_id: null,
      enabled,
      base_url: baseUrl.trim() || null,
      // Empty text means "use the default" (an empty path would be refused).
      options: Object.fromEntries(Object.entries(options).map(([k, v]) => [k, typeof v === 'string' ? v.trim() || null : v])),
      secrets: Object.fromEntries(def.secrets.map((s) => [s.key, leftEmpty(s.key, s.required_when_enabled) ? { source: 'KEEP' as const } : secretInput(secrets[s.key] ?? emptySecret(false))])),
      reason: reason.trim(),
    });
    if (res) {
      setSecrets(Object.fromEntries(def.secrets.map((s) => [s.key, emptySecret(true)])));
      onDone();
    }
  };
  if (write.state.phase === 'done') {
    const r = write.state.result;
    return (
      <div className="space-y-4">
        <WriteOutcome result={r.result} approval={r.approval} warnings={r.warnings} env={env} appliedText={t(`applyMode.settings.${def.apply_modes.settings}`, { seconds: ctx.refresh_seconds })} />
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </div>
    );
  }
  const fieldInput = (f: ProviderFieldDefinition) =>
    f.key === ASK_AFTER_FAILURES ? (
      <AskWhenField
        key={f.key}
        value={options[f.key] === null || options[f.key] === undefined ? null : Number(options[f.key])}
        onChange={(v) => setOptions((o) => ({ ...o, [f.key]: v }))}
        max={f.max}
      />
    ) : f.type === 'integer' ? (
      <NumberField
        key={f.key}
        label={tx(`provider.field.${f.key}`, f.key)}
        value={options[f.key] === null || options[f.key] === undefined ? null : Number(options[f.key])}
        onChange={(v) => setOptions((o) => ({ ...o, [f.key]: v }))}
        min={f.min}
        max={f.max}
        unit={unitLabel(f.unit)}
      />
    ) : (
      <Field key={f.key} label={tx(`provider.field.${f.key}`, f.key)} hint={f.type === 'path' ? t('providerDialog.pathHint', { value: String(f.default ?? '') }) : tx(`provider.fieldHint.${f.key}`, '')}>
        <Input value={String(options[f.key] ?? '')} onChange={(e) => setOptions((o) => ({ ...o, [f.key]: e.target.value }))} className={f.type === 'path' ? 'font-mono' : undefined} autoComplete="off" spellCheck={false} />
      </Field>
    );
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} />
      {!detail.configured_in_database ? <Callout tone="info">{t('providerDialog.firstTime')}</Callout> : null}
      <SwitchRow
        label={t('providerDialog.enabled')}
        hint={
          <>
            <span className="block text-xs text-muted-foreground">{t(`provider.offEffect.${def.provider}`)}</span>
            <span className={def.apply_modes.enabled === 'RESTART' ? 'block text-xs font-medium text-warning' : 'block text-xs text-muted-foreground'}>
              {t(`applyMode.enabled.${def.apply_modes.enabled}`, { seconds: ctx.refresh_seconds })}
            </span>
          </>
        }
        checked={enabled}
        onChange={setEnabled}
      />
      <Field label={t('provider.field.base_url')} hint={httpInProduction ? <span className="text-critical">{t('envDialog.httpsRequired')}</span> : undefined}>
        <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} className="font-mono" autoComplete="off" spellCheck={false} placeholder="https://" />
      </Field>
      {def.presets.length ? (
        <PresetFill
          env={env}
          presets={def.presets.map((p) => ({ environment: p.environment, value: p }))}
          describe={(p) => p.base_url}
          matches={(p) => baseUrl.trim() === p.base_url}
          onUse={(p) => {
            setBaseUrl(p.base_url);
            setOptions((o) => ({ ...o, ...p.options }));
          }}
          note={(p) => (p.note ? <span className="block text-xs text-muted-foreground">{t(`warnings.${p.note}`)}</span> : null)}
        />
      ) : (
        <p className="text-xs text-muted-foreground">{t('vnnic.noPreset', { env: t(`env.inline.${env}`) })}</p>
      )}
      {basic.length ? <div className="grid items-start gap-3 sm:grid-cols-2">{basic.map(fieldInput)}</div> : null}
      {advanced.length ? (
        <details className="rounded-lg border p-3 text-sm">
          <summary className="cursor-pointer font-medium">{t('providerDialog.advanced')}</summary>
          <div className="mt-3 grid items-start gap-3 sm:grid-cols-2">{advanced.map(fieldInput)}</div>
        </details>
      ) : null}
      {def.secrets.map((s) => {
        const d = secrets[s.key] ?? emptySecret(false);
        const configured = !!base?.secrets[s.key]?.configured && !base.secrets[s.key]?.destroyed;
        return (
          <SecretField
            key={s.key}
            label={tx(`provider.secret.${s.key}`, s.key)}
            draft={d}
            onChange={(next) => setSecrets((x) => ({ ...x, [s.key]: next }))}
            canKeep={configured}
            keepLabel={t('providerDialog.keep')}
            env={env}
            hint={!configured && s.required_when_enabled ? t('providerDialog.secretRequired') : undefined}
          />
        );
      })}
      {secretTouched ? <p className="text-xs text-muted-foreground">{t('providerDialog.secretPermission')}</p> : null}
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={!valid || write.busy} variant={production ? 'destructive' : 'default'}>
          <Save /> {write.busy ? t('common.saving') : production ? t('form.submitForApproval') : t('form.saveNow')}
        </Button>
      </DialogFooter>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sponsoring registrars (gTLD reporting)

function SponsoringRegistrars({ tick, onChanged }: { tick: number; onChanged: () => void }) {
  const { t } = useTranslation('integrations');
  const [editing, setEditing] = useState<SponsoringRegistrar | null>(null);
  const [local, setLocal] = useState(0);
  const result = useApi((signal) => api.listSponsoringRegistrars({ signal }), [tick, local]);
  const data = useRetained(result.data);
  const columns: Column<SponsoringRegistrar>[] = [
    { key: 'c', header: t('sponsor.connection'), cell: (s) => <ConnectionLink connection={s.connection} /> },
    {
      key: 'src',
      header: t('sponsor.sourceLabel'),
      cell: (s) => <Badge tone={s.source === 'MISSING' ? 'crit' : s.source === 'UPSTREAM_REGISTRAR' ? 'info' : 'ok'}>{t(`sponsor.source.${s.source}`)}</Badge>,
    },
    { key: 'iana', header: t('sponsor.iana'), cell: (s) => (s.iana_id !== null ? <span className="tabular">{s.iana_id}</span> : '—'), numeric: true },
    { key: 'name', header: t('sponsor.name'), cell: (s) => s.trade_name ?? '—' },
    { key: 'zones', header: t('sponsor.zones'), cell: (s) => formatCount(s.gtld_zones), numeric: true },
    { key: 'domains', header: t('sponsor.domains'), cell: (s) => formatCount(s.gtld_domains_held), numeric: true },
    {
      key: 'act',
      header: '',
      cell: (s) =>
        s.pending ? (
          <span className="grid gap-0.5 text-xs">
            <StatusBadge group="approvalStatus" value={s.pending.approval.status} />
            <span className="text-muted-foreground">
              → {s.pending.iana_id} · {s.pending.trade_name}
            </span>
          </span>
        ) : s.editable ? (
          <Button size="xs" variant="outline" onClick={() => setEditing(s)}>
            <Pencil /> {s.source === 'MISSING' ? t('sponsor.set') : t('sponsor.edit')}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">{t('sponsor.fromUpstream')}</span>
        ),
    },
  ];
  return (
    <Panel
      title={t('sponsor.title')}
      description={t('sponsor.description')}
      actions={
        <Button size="sm" variant="ghost" onClick={() => setLocal((n) => n + 1)} aria-label={t('common.refresh')}>
          <RefreshCw />
        </Button>
      }
      flush
    >
      {result.status === 'error' ? (
        <div className="px-5 pb-5">
          <DataError error={result.error} onRetry={result.reload} />
        </div>
      ) : !data ? (
        <div className="px-5">
          <Loading rows={2} />
        </div>
      ) : (
        <>
          {data.items.some((s) => s.source === 'MISSING') ? (
            <div className="px-5 pb-3">
              <Callout tone="crit">{t('sponsor.missing')}</Callout>
            </div>
          ) : null}
          <DataTable columns={columns} rows={data.items} rowKey={(s) => s.connection.id} empty={<EmptyState title={t('sponsor.none')} />} />
        </>
      )}
      <ChangeDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title={editing ? t('sponsor.dialogTitle', { name: editing.connection.name }) : ''} description={t('sponsor.dialogDescription')}>
        {editing ? (
          <SponsorForm
            row={editing}
            onClose={() => setEditing(null)}
            onDone={() => {
              setLocal((n) => n + 1);
              onChanged();
            }}
          />
        ) : null}
      </ChangeDialog>
    </Panel>
  );
}

function SponsorForm({ row, onClose, onDone }: { row: SponsoringRegistrar; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('integrations');
  const [iana, setIana] = useState(row.iana_id !== null ? String(row.iana_id) : '');
  const [name, setName] = useState(row.trade_name ?? '');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: SponsoringRegistrarBody, opts) => api.proposeSponsoringRegistrar(row.connection.code, body, opts));
  const ianaNumber = Number(iana);
  const valid = Number.isInteger(ianaNumber) && ianaNumber > 0 && name.trim() && reason.trim() && confirmed;
  const submit = async () => {
    const res = await write.run({ iana_id: ianaNumber, trade_name: name.trim(), expected_updated_at: row.source === 'MISSING' ? null : row.updated_at, reason: reason.trim() });
    if (res) onDone();
  };
  if (write.state.phase === 'done') {
    const r = write.state.result;
    return (
      <div className="space-y-4">
        <WriteOutcome result={r.result} approval={r.approval} warnings={r.warnings} />
        <DialogFooter>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <Callout tone="pending" title={t('sponsor.alwaysApproval')}>
        {t('sponsor.alwaysApprovalBody')}
      </Callout>
      <div className="grid items-start gap-3 sm:grid-cols-2">
        <Field label={t('upstream.ianaId')}>
          <Input value={iana} onChange={(e) => setIana(e.target.value.replace(/\D/g, ''))} inputMode="numeric" />
        </Field>
        <Field label={t('upstream.tradeName')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={300} />
        </Field>
      </div>
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} text={t('sponsor.confirm')} />
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={!valid || write.busy}>
          <Save /> {t('form.submitForApproval')}
        </Button>
      </DialogFooter>
    </div>
  );
}
